import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'src/utils/axiosInstance';

const initialState = {
  countries: [],
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,
};

export const countrySlice = createSlice({
  name: 'country',
  initialState,
  reducers: {
    setCountries: (state, action) => {
      state.countries = action.payload;
    },
    setTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const { setCountries, setTotalCount, setTotalPages, setLoading, setError } = countrySlice.actions;

// Get countries (paginated & searchable)
export const getCountries = (filters) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const page = filters.page || 1;
    const limit = filters.limit || 10;
    const search = filters.search || '';
    const response = await axiosInstance.get('/admin/country/list', {
      params: { page, limit, search }
    });
    if (response.data && response.data.status === true) {
      const result = response.data.data.result || [];
      const totalCount = response.data.data.totalCount || result.length;
      const totalPages = response.data.data.totalPages || 1;
      dispatch(setCountries(result));
      dispatch(setTotalCount(totalCount));
      dispatch(setTotalPages(totalPages));
      return { status: true, data: result, totalCount, totalPages };
    } else {
      dispatch(setCountries([]));
      dispatch(setTotalCount(0));
      dispatch(setTotalPages(0));
      const errorMsg = response.data?.message || 'Failed to fetch countries';
      dispatch(setError(errorMsg));
      return { status: false, message: errorMsg };
    }
  } catch (error) {
    dispatch(setCountries([]));
    dispatch(setTotalCount(0));
    dispatch(setTotalPages(0));
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch countries';
    dispatch(setError(errorMsg));
    return { status: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Get single country details (prefill edit form)
export const getCountryDetails = (id) => async () => {
  try {
    const response = await axiosInstance.get(`/admin/country/getbyId/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data };
    } else {
      return { status: false, message: response.data?.message || 'Failed to fetch country details' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch country details';
    return { status: false, message: errorMsg };
  }
};

// Add new country
export const addCountry = (payload) => async () => {
  try {
    const response = await axiosInstance.post('/admin/country/add', payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to add country' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to add country';
    return { status: false, message: errorMsg };
  }
};

// Update country details
export const updateCountry = (id, payload) => async () => {
  try {
    const response = await axiosInstance.put(`/admin/country/update/${id}`, payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to update country' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to update country';
    return { status: false, message: errorMsg };
  }
};

// Delete country
export const deleteCountry = (id) => async () => {
  try {
    const response = await axiosInstance.delete(`/admin/country/delete/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to delete country' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to delete country';
    return { status: false, message: errorMsg };
  }
};

export default countrySlice.reducer;
